import { Cause, Effect, Exit, Layer, Ref, Scope, Semaphore } from "effect";
import type { EditorDocumentIdentity } from "./documents-model.ts";
import { EditorDocuments } from "./documents-service.ts";
import { EditorLanguageIo } from "./language-service.ts";
import { EditorModelIo } from "./model-service.ts";
import { editorModelAuthority, EditorModelInputFailure, type EditorModelHandle } from "./model.ts";
import { EditorProjects, EditorProjectTypes, type EditorProject, type EditorProjectFailure } from "./project-service.ts";
import { makeScopedCache } from "../state/resource-cache.ts";
import { makeRequestScopes, type OwnedRequest } from "../state/request-scopes.ts";
export const makeEditorProjects = Effect.fn("Frontend.EditorProjects.make")(function* (root: Scope.Scope) {
  const documents = yield* EditorDocuments, models = yield* EditorModelIo, languages = yield* EditorLanguageIo, types = yield* EditorProjectTypes;
  const rootId = yield* models.allocateRoot, failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  const retain = (cause: Cause.Cause<never>) => Ref.update(failures, current => [...current, ...cause.reasons.filter(reason => !current.includes(reason))]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const key = (value: EditorDocumentIdentity) => JSON.stringify([value.workspaceId, value.generation]);
  const load = Effect.fn("Frontend.EditorProjects.load")(function* (encoded: string) {
    const initial = yield* documents.read;
    if (initial.closed || root.state._tag === "Closed" || key(initial) !== encoded)
      return yield* new EditorModelInputFailure();
    const target: EditorDocumentIdentity = { workspaceId: initial.workspaceId, generation: initial.generation }, lifetime = yield* models.allocateRoot;
    const authority = editorModelAuthority(rootId, JSON.stringify(target.workspaceId), lifetime), owner = yield* Scope.Scope;
    const requests = yield* makeRequestScopes(owner, exit => Exit.isFailure(exit) ? retain(exit.cause) : Effect.void);
    const state = yield* Ref.make<ReadonlyMap<string, {
      readonly handle: EditorModelHandle;
      readonly owned: OwnedRequest;
    }>>(new Map());
    const gate = yield* Semaphore.make(1), revision = yield* Ref.make(-1);
    const active = Effect.gen(function* () {
      const current = yield* documents.read; if (current.closed || root.state._tag === "Closed" || owner.state._tag === "Closed" || key(current) !== encoded)
        return yield* new EditorModelInputFailure(); return current;
    });
    const libraries = yield* types.libraries(initial.files, authority, initial.revision);
    const language = yield* languages.acquire({ authority, libraries });
    const synchronize = gate.withPermit(Effect.gen(function* () {
      const current = yield* active;
      if ((yield* Ref.get(revision)) === current.revision)
        return;
      for (const [filename, member] of yield* Ref.get(state))
        if (!Object.hasOwn(current.files, filename)) {
          yield* member.owned.close;
          yield* Ref.update(state, value => { const next = new Map(value); next.delete(filename); return next; });
        }
      for (const [filename, contents] of Object.entries(current.files)) {
        const member = (yield* Ref.get(state)).get(filename);
        if (member) {
          yield* models.write(member.handle, contents);
          continue;
        }
        const owned = yield* requests.open;
        const handle = yield* models.acquire({ rootId, workspaceId: JSON.stringify(target.workspaceId), lifetime, filename, contents, languageId: language.languageFor(filename) }).pipe(Effect.provideService(Scope.Scope, owned.scope));
        yield* Ref.update(state, value => new Map(value).set(filename, { handle, owned }));
      }
      yield* active;
      const nextLibraries = yield* types.libraries(current.files, authority, current.revision);
      yield* language.updateLibraries(nextLibraries);
      yield* language.synchronize([...(yield* Ref.get(state)).values()].map(value => value.handle.model.uri.toString()));
      yield* Ref.set(revision, current.revision);
    }));
    yield* synchronize;
    const model = (filename: string) => synchronize.pipe(Effect.andThen(Ref.get(state)), Effect.map(value => value.get(filename)?.handle));
    const hover = (filename: string, offset: number) => Effect.gen(function* () {
      const handle = yield* model(filename); if (!handle)
        return yield* new EditorModelInputFailure(); return yield* language.hover(handle.model.uri.toString(), offset);
    });
    return { authority, target, synchronize, model, hover } satisfies EditorProject;
  });
  const cache = yield* makeScopedCache<string, EditorProject, EditorProjectFailure>(16, load, retain, { releaseIdle: true });
  const use = <A, E, R>(target: EditorDocumentIdentity, body: (project: EditorProject) => Effect.Effect<A, E, R>) => Effect.suspend(() => root.state._tag === "Closed" ? Effect.interrupt : cache.use(key(target), body));
  return EditorProjects.of({ use, inspect: cache.inspect });
});
// Partial graph acquisition is fatal; use-time project failures stay declared.
export const EditorProjectsLive = (root: Scope.Scope) => Layer.effect(EditorProjects, makeEditorProjects(root).pipe(Effect.orDie));
